import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Notification,
  NotificationStatus,
  NotificationType,
  NotificationStats,
  NotificationWebSocketEvent,
  NotificationFilters,
} from '../../types/notification.types';

interface NotificationsState {
  // Datos
  notifications: Notification[];
  stats: NotificationStats | null;
  
  // Estado de carga
  loading: boolean;
  loadingStats: boolean;
  
  // Estado de conexión WebSocket
  wsConnected: boolean;
  wsConnecting: boolean;
  wsError: string | null;
  
  // Filtros y paginación
  filters: {
    status?: NotificationStatus;
    type?: NotificationType;
    limit: number;
    offset: number;
  };
  
  // Estado de la UI
  showDropdown: boolean;
  lastFetchTime: number | null;
  
  // Errores
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  stats: null,
  loading: false,
  loadingStats: false,
  wsConnected: false,
  wsConnecting: false,
  wsError: null,
  filters: {
    limit: 20,
    offset: 0,
  },
  showDropdown: false,
  lastFetchTime: null,
  error: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Acciones de carga
    fetchNotificationsRequest: (
      state,
      _action: PayloadAction<{ filters?: NotificationFilters } | undefined>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchNotificationsSuccess: (state, action: PayloadAction<{ notifications: Notification[]; total: number }>) => {
      state.loading = false;
      state.notifications = action.payload.notifications;
      state.lastFetchTime = Date.now();
    },
    fetchNotificationsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Acciones de estadísticas
    fetchStatsRequest: (state) => {
      state.loadingStats = true;
    },
    fetchStatsSuccess: (state, action: PayloadAction<NotificationStats>) => {
      state.loadingStats = false;
      state.stats = action.payload;
    },
    fetchStatsFailure: (state, action: PayloadAction<string>) => {
      state.loadingStats = false;
      state.error = action.payload;
    },

    // Acciones de WebSocket
    wsConnectRequest: (state) => {
      state.wsConnecting = true;
      state.wsError = null;
    },
    wsConnectSuccess: (state) => {
      state.wsConnecting = false;
      state.wsConnected = true;
      state.wsError = null;
    },
    wsConnectFailure: (state, action: PayloadAction<string>) => {
      state.wsConnecting = false;
      state.wsConnected = false;
      state.wsError = action.payload;
    },
    wsDisconnect: (state) => {
      state.wsConnected = false;
      state.wsConnecting = false;
    },
    wsEventReceived: (state, action: PayloadAction<NotificationWebSocketEvent>) => {
      const { type, data } = action.payload;
      
      switch (type) {
        case 'notification_created':
          // Asegurar que notifications sea un array
          if (!state.notifications) {
            state.notifications = [];
          }
          // Agregar nueva notificación al inicio
          state.notifications.unshift(data);
          // Actualizar estadísticas
          if (state.stats) {
            state.stats.total += 1;
            state.stats.unread += 1;
            
            // Asegurar que byType y byStatus existan
            if (!state.stats.byType) {
              state.stats.byType = {} as Record<NotificationType, number>;
            }
            if (!state.stats.byStatus) {
              state.stats.byStatus = {} as Record<NotificationStatus, number>;
            }
            
            state.stats.byType[data.type] = (state.stats.byType[data.type] || 0) + 1;
            state.stats.byStatus[data.status] = (state.stats.byStatus[data.status] || 0) + 1;
          }
          break;
          
        case 'notification_updated':
          // Actualizar notificación existente
          if (state.notifications) {
            const updateIndex = state.notifications.findIndex(n => n.id === data.id);
            if (updateIndex !== -1) {
              state.notifications[updateIndex] = data;
            }
          }
          break;
          
        case 'notification_deleted':
          // Remover notificación
          if (state.notifications) {
            state.notifications = state.notifications.filter(n => n.id !== data.id);
            // Actualizar estadísticas
            if (state.stats) {
              state.stats.total = Math.max(0, state.stats.total - 1);
              if (data.status === NotificationStatus.UNREAD) {
                state.stats.unread = Math.max(0, state.stats.unread - 1);
              }
            }
          }
          break;
      }
    },

    // Acciones de marcado como leído
    markAsReadRequest: (state, action: PayloadAction<string[]>) => {
      // Optimistic update
      action.payload.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification && notification.status === NotificationStatus.UNREAD) {
          notification.status = NotificationStatus.READ;
          notification.readAt = new Date().toISOString();
        }
      });
      
      // Actualizar estadísticas
      if (state.stats) {
        const markedCount = action.payload.length;
        state.stats.unread = Math.max(0, state.stats.unread - markedCount);
        
        // Asegurar que byStatus exista
        if (!state.stats.byStatus) {
          state.stats.byStatus = {} as Record<NotificationStatus, number>;
        }
        
        state.stats.byStatus[NotificationStatus.UNREAD] = Math.max(0, 
          state.stats.byStatus[NotificationStatus.UNREAD] - markedCount);
        state.stats.byStatus[NotificationStatus.READ] = 
          (state.stats.byStatus[NotificationStatus.READ] || 0) + markedCount;
      }
    },
    markAsReadSuccess: () => {
      // Confirmar cambios (ya aplicados optimísticamente)
    },
    markAsReadFailure: (state, action: PayloadAction<{ ids: string[]; error: string }>) => {
      // Revertir cambios optimísticos
      action.payload.ids.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification) {
          notification.status = NotificationStatus.UNREAD;
          notification.readAt = undefined;
        }
      });

      // GH#2667 — y devolver el contador: `markAsReadRequest` lo bajó en `ids.length`; revertir solo la
      // lista dejaba la campana con menos no leídas de las que hay (simétrico a la petición).
      if (state.stats) {
        const n = action.payload.ids.length;
        state.stats.unread = state.stats.unread + n;
        if (!state.stats.byStatus) {
          state.stats.byStatus = {} as Record<NotificationStatus, number>;
        }
        state.stats.byStatus[NotificationStatus.UNREAD] = (state.stats.byStatus[NotificationStatus.UNREAD] || 0) + n;
        state.stats.byStatus[NotificationStatus.READ] = Math.max(0, (state.stats.byStatus[NotificationStatus.READ] || 0) - n);
      }
      state.error = action.payload.error;
    },

    // Acciones de marcado como no leído
    markAsUnreadRequest: (state, action: PayloadAction<string[]>) => {
      // Optimistic update
      action.payload.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification && notification.status === NotificationStatus.READ) {
          notification.status = NotificationStatus.UNREAD;
          notification.readAt = undefined;
        }
      });
      
      // Actualizar estadísticas
      if (state.stats) {
        const markedCount = action.payload.length;
        state.stats.unread += markedCount;
        
        // Asegurar que byStatus exista
        if (!state.stats.byStatus) {
          state.stats.byStatus = {} as Record<NotificationStatus, number>;
        }
        
        state.stats.byStatus[NotificationStatus.READ] = Math.max(0, 
          state.stats.byStatus[NotificationStatus.READ] - markedCount);
        state.stats.byStatus[NotificationStatus.UNREAD] = 
          (state.stats.byStatus[NotificationStatus.UNREAD] || 0) + markedCount;
      }
    },
    markAsUnreadSuccess: () => {
      // Confirmar cambios (ya aplicados optimísticamente)
    },
    markAsUnreadFailure: (state, action: PayloadAction<{ ids: string[]; error: string }>) => {
      // Revertir cambios optimísticos
      action.payload.ids.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification) {
          notification.status = NotificationStatus.READ;
          notification.readAt = new Date().toISOString();
        }
      });
      state.error = action.payload.error;
    },

    // Marcar todas como leídas
    markAllAsReadRequest: (state) => {
      // Optimistic update
      state.notifications.forEach(notification => {
        if (notification.status === NotificationStatus.UNREAD) {
          notification.status = NotificationStatus.READ;
          notification.readAt = new Date().toISOString();
        }
      });
      
      // Actualizar estadísticas
      if (state.stats) {
        const unreadCount = state.stats.unread;
        state.stats.unread = 0;
        
        // Asegurar que byStatus exista
        if (!state.stats.byStatus) {
          state.stats.byStatus = {} as Record<NotificationStatus, number>;
        }
        
        state.stats.byStatus[NotificationStatus.UNREAD] = 0;
        state.stats.byStatus[NotificationStatus.READ] = 
          (state.stats.byStatus[NotificationStatus.READ] || 0) + unreadCount;
      }
    },
    markAllAsReadSuccess: () => {
      // Confirmar cambios (ya aplicados optimísticamente)
    },
    markAllAsReadFailure: (state, action: PayloadAction<string>) => {
      // Revertir cambios optimísticos
      state.notifications.forEach(notification => {
        if (notification.readAt) {
          notification.status = NotificationStatus.UNREAD;
          notification.readAt = undefined;
        }
      });
      state.error = action.payload;
    },

    // Eliminar notificación
    deleteNotificationRequest: (state, action: PayloadAction<string>) => {
      // Optimistic update
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    deleteNotificationSuccess: () => {
      // Confirmar eliminación (ya aplicada optimísticamente)
    },
    deleteNotificationFailure: (state, action: PayloadAction<{ id: string; error: string }>) => {
      // Revertir eliminación optimística
      state.error = action.payload.error;
    },

    // Acciones de UI
    toggleDropdown: (state) => {
      state.showDropdown = !state.showDropdown;
    },
    closeDropdown: (state) => {
      state.showDropdown = false;
    },
    setFilters: (state, action: PayloadAction<Partial<NotificationsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.stats = null;
      state.lastFetchTime = null;
    },
  },
});

export const {
  // Carga
  fetchNotificationsRequest,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  
  // Estadísticas
  fetchStatsRequest,
  fetchStatsSuccess,
  fetchStatsFailure,
  
  // WebSocket
  wsConnectRequest,
  wsConnectSuccess,
  wsConnectFailure,
  wsDisconnect,
  wsEventReceived,
  
  // Marcado como leído
  markAsReadRequest,
  markAsReadSuccess,
  markAsReadFailure,
  markAsUnreadRequest,
  markAsUnreadSuccess,
  markAsUnreadFailure,
  markAllAsReadRequest,
  markAllAsReadSuccess,
  markAllAsReadFailure,
  
  // Eliminar
  deleteNotificationRequest,
  deleteNotificationSuccess,
  deleteNotificationFailure,
  
  // UI
  toggleDropdown,
  closeDropdown,
  setFilters,
  clearError,
  clearAllNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
